import { Client } from "@hapi/nes/lib/client"
import { ArrowBack, Clear, Send } from "@mui/icons-material"
import ArrowForward from "@mui/icons-material/ArrowForward"
import { ButtonBase, Fade } from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { withTheme } from "@mui/styles"
import linkifyHtml from "linkify-html"
import moment from "moment"
import queryStringify from "querystringify"
import { Component, useEffect } from "react"
import Confetti from "react-dom-confetti"
import Helmet from "react-helmet"
import { withTranslation } from "react-i18next"
import { withRouter } from "react-router-dom"
import logo from "../assets/logo.svg"
import { sanitizeYaml, secondsToTime } from "../utilities"
import NoHost from "./NoHost"
import DifferentClues from "./rooms/DifferentClues"
import Embed from "./rooms/Embed"
import Hotspots from "./rooms/Hotspots"
import MultipleChoice from "./rooms/MultipleChoice"
import MultipleLists from "./rooms/MultipleLists"
import MultipleOpenEnded from "./rooms/MultipleOpenEnded"
import OpenEnded from "./rooms/OpenEnded"
import Picture from "./rooms/Picture"
import SortableList from "./rooms/SortableList"
import TextOnly from "./rooms/TextOnly"
import UserDifferentClues from "./rooms/UserDifferentClues"
import UserWelcome from "./UserWelcome"

const generateNaturalLanguageList = (array, t) => {
  if (array.length === 1) {
    return array[0]
  }
  const firsts = array.slice(0, array.length - 1)
  const last = array[array.length - 1]

  return firsts.join(", ") + " " + t`or` + " " + last
}

const LoadingScreen = ({ reconnect }) => {
  useEffect(() => {
    reconnect(
      queryStringify.parse(window.location.search).room,
      JSON.parse(localStorage.getItem("idRoomPairs")).find(
        ({ code }) => code === queryStringify.parse(window.location.search).room
      ).participantId
    )
  }, [reconnect])

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <CircularProgress />
    </div>
  )
}

export default withTheme(
  withTranslation()(
    withRouter(
      class UserRoom extends Component {
        state = {
          room: null,
          name: "",
          backgrounds: [],
          pictures: [],
          joinRoomLoading: false,
          joinRoomError: false,
          timer: null,
          greaterThan1155: null,
          greaterThan1307: null,
          greaterThan1530: null,
          confetti: false,
          solvedSlides: [],
          nextVisible: false,
        }

        updateDimensions = () => {
          const { greaterThan1155, greaterThan1307, greaterThan1530 } =
            this.state

          if (window.innerWidth > 1155) {
            !greaterThan1155 && this.setState({ greaterThan1155: true })
          } else {
            greaterThan1155 && this.setState({ greaterThan1155: false })
          }

          if (window.innerWidth > 1307) {
            !greaterThan1307 && this.setState({ greaterThan1307: true })
          } else {
            greaterThan1307 && this.setState({ greaterThan1307: false })
          }

          if (window.innerWidth > 1530) {
            !greaterThan1530 && this.setState({ greaterThan1530: true })
          } else {
            greaterThan1530 && this.setState({ greaterThan1530: false })
          }
        }

        setTimer = (room) => {
          if (room?.timerStartDate) {
            this.setState({
              timer: room.timerEndDate
                ? moment(room.timerEndDate).diff(
                    moment(room.timerStartDate),
                    "seconds"
                  )
                : moment().diff(moment(room.timerStartDate), "seconds"),
            })

            let interval = setInterval(() => {
              //get timerEndDate from state in order to stop the timer when a timer subscription arrives
              if (
                !this.state.room.timerEndDate &&
                this.state.room.timerStartDate
              ) {
                this.setState({
                  timer: moment().diff(moment(room.timerStartDate), "seconds"),
                })
              } else {
                clearInterval(interval)
              }
            }, 1000)
          }
        }

        joinRoom = async (code, name) => {
          const { history } = this.props

          this.setState({ joinRoomLoading: true })

          let room = await fetch(
            `${
              process.env.REACT_APP_SERVER_URL
            }/room/join?roomCode=${code.replace("-", "")}&name=${name}`,
            {
              method: "POST",
            }
          )
            .then(async (response) => {
              const room = await response.json()

              if (response.status !== 200) {
                this.setState({ joinRoomError: true })
                return null
              }

              const client = new Client(
                process.env.REACT_APP_SERVER_URL.replace("https", "wss")
              )
              const start = async () => {
                await client.connect()

                client.message("User " + room.participantId)

                client.subscribe(
                  "/room/" + code.replace("-", ""),
                  (payload) => {
                    this.setTimer(payload)

                    if (payload.confetti) {
                      this.setState({ confetti: true })
                    }

                    this.setState(({ room }) => ({
                      room: { ...room, ...payload },
                    }))
                  }
                )
              }

              start()

              if (JSON.parse(localStorage.getItem("idRoomPairs"))) {
                localStorage.setItem(
                  "idRoomPairs",
                  JSON.stringify([
                    ...JSON.parse(localStorage.getItem("idRoomPairs")).filter(
                      ({ code: roomCode }) => roomCode !== code
                    ),
                    { code, participantId: room.participantId },
                  ])
                )
              } else {
                localStorage.setItem(
                  "idRoomPairs",
                  JSON.stringify([{ code, participantId: room.participantId }])
                )
              }

              return room
            })
            .catch(() =>
              this.setState({
                joinRoomError: true,
              })
            )

          this.setState({ room })

          if (room) {
            if (room) {
              this.setTimer(room)
            }
            if (!queryStringify.parse(window.location.search).room) {
              history.push(`/?experience=${room.experience.id}&room=${code}`)
            }

            this.fetchBackgrounds(room.experience.slides)
          } else {
            this.setState({ timer: 0 })
          }

          this.setState({ joinRoomLoading: false })
        }

        reconnect = async (code, participantId) => {
          const { history } = this.props

          this.setState({ joinRoomLoading: true })

          let room = await fetch(
            `${
              process.env.REACT_APP_SERVER_URL
            }/room/reconnect?roomCode=${code.replace(
              "-",
              ""
            )}&participantId=${participantId}`,
            {
              method: "POST",
            }
          )
            .then(async (response) => {
              const room = await response.json()

              if (response.status !== 200) {
                this.setState({ joinRoomError: true })
                return null
              }

              const client = new Client(
                process.env.REACT_APP_SERVER_URL.replace("https", "wss")
              )
              const start = async () => {
                await client.connect()

                client.message("User " + room.participantId)

                client.subscribe(
                  "/room/" + code.replace("-", ""),
                  (payload) => {
                    this.setTimer(payload)

                    if (payload.confetti) {
                      this.setState({ confetti: true })
                    }

                    this.setState(({ room }) => ({
                      room: { ...room, ...payload },
                    }))
                  }
                )
              }

              start()

              if (JSON.parse(localStorage.getItem("idRoomPairs"))) {
                localStorage.setItem(
                  "idRoomPairs",
                  JSON.stringify([
                    ...JSON.parse(localStorage.getItem("idRoomPairs")).filter(
                      ({ code: roomCode }) => roomCode !== code
                    ),
                    { code, participantId: room.participantId },
                  ])
                )
              } else {
                localStorage.setItem(
                  "idRoomPairs",
                  JSON.stringify([{ code, participantId: room.participantId }])
                )
              }

              return room
            })
            .catch(() =>
              this.setState({
                joinRoomError: true,
              })
            )

          this.setState({ room })

          if (room) {
            if (room) {
              this.setTimer(room)
            }
            if (!queryStringify.parse(window.location.search).room) {
              history.push(`/?experience=${room.experience.id}&room=${code}`)
            }

            this.fetchBackgrounds(room.experience.slides)
          } else {
            this.setState({ timer: 0 })
          }

          this.setState({ joinRoomLoading: false })
        }

        fetchBackgrounds = async (slides) => {
          await slides.forEach(async (slide, index) => {
            if (slide.tipo === "solo-immagine") {
              await fetch(
                `${process.env.REACT_APP_STORAGE_URL}/${
                  queryStringify.parse(window.location.search).experience
                }/${slide.immagine}?password=${localStorage.getItem(
                  "password"
                )}`
              )
                .then(async (response) => {
                  if (response.status !== 200) {
                    this.setState(({ pictures }) => {
                      pictures[index] = null

                      return {
                        pictures,
                      }
                    })

                    return null
                  }

                  return await response.blob()
                })
                .then((imageBlob) => {
                  if (imageBlob) {
                    const imageObjectURL = URL.createObjectURL(imageBlob)

                    this.setState(({ pictures }) => {
                      pictures[index] = imageObjectURL

                      return {
                        pictures,
                      }
                    })
                  }
                })
            }

            await fetch(
              `${process.env.REACT_APP_STORAGE_URL}/${
                queryStringify.parse(window.location.search).experience
              }/${slide.sfondo}?password=${localStorage.getItem("password")}`
            )
              .then(async (response) => {
                if (response.status !== 200) {
                  this.setState(({ backgrounds }) => {
                    backgrounds[index] = null

                    return {
                      backgrounds,
                    }
                  })

                  return null
                }

                return await response.blob()
              })
              .then((imageBlob) => {
                // Then create a local URL for that image and print it
                if (imageBlob) {
                  const imageObjectURL = URL.createObjectURL(imageBlob)

                  this.setState(({ backgrounds }) => {
                    backgrounds[index] = imageObjectURL

                    return {
                      backgrounds,
                    }
                  })
                }
              })
          })
        }

        changeSlide = (roomCode, participantId, isNext) =>
          fetch(
            `${process.env.REACT_APP_SERVER_URL}/room/${
              isNext ? "next" : "previous"
            }Slide?roomCode=${roomCode}&participantId=${participantId}`,
            {
              method: "POST",
              headers: {},
            }
          ).then(() => this.setState({ nextVisible: false }))

        checkSolution = async (roomCode, participantId, currentSlide) =>
          fetch(
            `${process.env.REACT_APP_SERVER_URL}/room/checkSolution?roomCode=${roomCode}&participantId=${participantId}`,
            {
              method: "POST",
              headers: {},
            }
          ).then((response) =>
            response.json().then((isAnswerCorrect) => {
              if (isAnswerCorrect) {
                this.setState(({ solvedSlides }) => ({
                  solvedSlides: [...solvedSlides, currentSlide],
                }))
              } else {
                this.setState({ showWrongAnswerIcon: true })

                setTimeout(
                  () => this.setState({ showWrongAnswerIcon: false }),
                  1000
                )
              }
            })
          )

        componentDidMount() {
          const { adminRoom } = this.props

          if (adminRoom?.experience.slides) {
            this.fetchBackgrounds(adminRoom.experience.slides)
          }

          if (
            JSON.parse(localStorage.getItem("idRoomPairs"))?.find(
              ({ code }) =>
                code === queryStringify.parse(window.location.search).room
            )
          ) {
            this.reconnect(
              queryStringify.parse(window.location.search).room,
              JSON.parse(localStorage.getItem("idRoomPairs")).find(
                ({ code }) =>
                  code === queryStringify.parse(window.location.search).room
              ).participantId
            )
          }

          this.updateDimensions()
          window.addEventListener("resize", this.updateDimensions)
        }

        componentWillUnmount() {
          window.removeEventListener("resize", this.updateDimensions)
        }

        componentWillReceiveProps(nextProps) {
          if (this.props.confetti !== nextProps.confetti) {
            this.setState({
              confetti: true,
            })

            nextProps.setConfetti(false)
          }
        }

        render() {
          const {
            t,
            adminRoom,
            isNextPage,
            nextPageHeight,
            measureRef,
            parentAspectRatio,
            theme,
          } = this.props
          const {
            room: userRoom,
            name,
            backgrounds,
            pictures,
            joinRoomLoading,
            joinRoomError,
            timer,
            greaterThan1155,
            greaterThan1307,
            greaterThan1530,
            confetti,
            solvedSlides,
            nextVisible,
            showWrongAnswerIcon,
          } = this.state

          const room =
            window.location.pathname === "/admin" ? adminRoom : userRoom

          if (confetti) {
            this.setState({
              confetti: false,
            })
          }

          const isVerifyButtonVisible =
            room &&
            !solvedSlides.includes(room.currentSlide) &&
            (room.experience.slides[room.currentSlide].tipo ===
              "lista-da-ordinare" ||
              room.experience.slides[room.currentSlide].tipo ===
                "doppia-lista" ||
              room.experience.slides[room.currentSlide].tipo ===
                "lista-multipla")

          return (
            <>
              <div style={{ position: "absolute", bottom: 0, zIndex: 1000 }}>
                <Confetti
                  active={confetti}
                  config={{
                    angle: 45,
                    spread: 45,
                    startVelocity: 70,
                    elementCount: 200,
                    dragFriction: 0.12,
                    duration: 5000,
                    stagger: 0,
                    width: "10px",
                    height: "10px",
                    perspective: "1000px",
                    colors: [
                      "#a864fd",
                      "#29cdff",
                      "#78ff44",
                      "#ff718d",
                      "#fdff6a",
                    ],
                  }}
                />
              </div>
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  zIndex: 1000,
                }}
              >
                <Confetti
                  active={confetti}
                  config={{
                    angle: 135,
                    spread: 45,
                    startVelocity: 70,
                    elementCount: 200,
                    dragFriction: 0.12,
                    duration: 5000,
                    stagger: 0,
                    width: "10px",
                    height: "10px",
                    perspective: "500px",
                    colors: [
                      "#a864fd",
                      "#29cdff",
                      "#78ff44",
                      "#ff718d",
                      "#fdff6a",
                    ],
                  }}
                />
              </div>
              <Helmet>
                <title>
                  Lapwing
                  {room?.experience?.name ? ` - ${room.experience.name}` : ""}
                </title>
              </Helmet>
              {window.location.pathname !== "/admin" && !room ? (
                queryStringify.parse(window.location.search).room ? (
                  JSON.parse(localStorage.getItem("idRoomPairs"))?.find(
                    ({ code }) =>
                      code === queryStringify.parse(window.location.search).room
                  ) ? (
                    <LoadingScreen reconnect={this.reconnect} />
                  ) : (
                    <div
                      style={{
                        backgroundColor: "#E6E2B1",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        textAlign: "center",
                      }}
                      className="notSelectable"
                    >
                      <img alt="" src={logo} style={{ marginBottom: "48px" }} />
                      <Typography
                        variant="h4"
                        style={{
                          fontSize: "30px",
                          fontWeight: 600,
                          margin: "0 32px 8px 32px",
                          color: "#181818",
                        }}
                      >
                        {t`What's your name?`}
                      </Typography>
                      <TextField
                        label={t`Name`}
                        variant="outlined"
                        style={{
                          marginTop: "48px",
                          width: "256px",
                          textTransform: "none",
                        }}
                        helperText={
                          joinRoomError ? t`Something went wrong` : " "
                        }
                        onKeyPress={(event) => {
                          if (event.key === "Enter" && name) {
                            this.joinRoom(
                              queryStringify.parse(window.location.search).room,
                              name
                            )
                          }
                        }}
                        onChange={(event) =>
                          this.setState({
                            name: event.target.value,
                          })
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                style={{
                                  color: "#181818",
                                  opacity: !name || joinRoomLoading ? 0.54 : 1,
                                }}
                                onClick={() =>
                                  this.joinRoom(
                                    queryStringify.parse(window.location.search)
                                      .room,
                                    name
                                  )
                                }
                                disabled={!name || joinRoomLoading}
                              >
                                <ArrowForward />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                  )
                ) : queryStringify.parse(window.location.search).experience ? (
                  <NoHost />
                ) : (
                  <UserWelcome
                    joinRoom={this.joinRoom}
                    joinRoomLoading={joinRoomLoading}
                    joinRoomError={joinRoomError}
                    setJoinRoomError={(joinRoomError) =>
                      this.setState({ joinRoomError })
                    }
                  />
                )
              ) : backgrounds[room.currentSlide] !== undefined ||
                room.experience.slides[room.currentSlide].tipo === "embed" ||
                (room.experience.slides[room.currentSlide].tipo ===
                  "solo-immagine" &&
                  pictures[room.currentSlide] !== undefined) ? (
                (!backgrounds[room.currentSlide] &&
                  room.experience.slides[room.currentSlide].tipo !== "embed") ||
                (room.experience.slides[room.currentSlide].tipo ===
                  "solo-immagine" &&
                  !pictures[room.currentSlide]) ? (
                  <div
                    style={{
                      widht: "100%",
                      height: nextPageHeight || "100%",
                      backgroundColor: "#E6E2B1",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    ref={measureRef}
                  >
                    <Typography
                      variant="h5"
                      style={{
                        textAlign: "center",
                        margin: "0 16px",
                      }}
                    >
                      {window.location.pathname === "/admin"
                        ? room.experience.slides[room.currentSlide].tipo ===
                            "solo-immagine" && !pictures[room.currentSlide]
                          ? t`Missing picture`
                          : t`Missing background`
                        : t`Cannot display page`}
                    </Typography>
                  </div>
                ) : isNextPage ? (
                  <div
                    style={{
                      width: "100%",
                      height: nextPageHeight,
                      backgroundImage: `url(${backgrounds[room.currentSlide]})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      overflow: "auto",
                    }}
                    className="notSelectable"
                    ref={measureRef}
                  >
                    {room.experience.slides[room.currentSlide].tipo ===
                    "solo-immagine" ? (
                      <img
                        alt=""
                        src={pictures[room.currentSlide]}
                        style={{
                          width: "calc(100% - 64px)",
                          height: "calc(100% - 64px)",
                          objectFit: "contain",
                          margin: "32px",
                        }}
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                      "indizi-diversi" ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          padding: "16px",
                          maxHeight: "calc(100% - 32px)",
                          overflow: "auto",
                        }}
                      >
                        {room.experience.slides[room.currentSlide].indizi.map(
                          (clue, index) => (
                            <div
                              style={{
                                textAlign: "center",
                                textTransform: "none",
                                marginTop: index === 0 ? 0 : "16px",
                                padding: "32px",
                                borderRadius: "24px",
                                background: "rgba(255, 255, 255, .8)",
                                backdropFilter: "blur(16px)",
                              }}
                            >
                              <Typography
                                variant="h4"
                                style={{
                                  fontWeight: 300,
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: linkifyHtml(sanitizeYaml(clue), {
                                    target: "_blank",
                                    attributes: {
                                      class: "underlineOnHover",
                                    },
                                  }),
                                }}
                              />
                            </div>
                          )
                        )}
                      </div>
                    ) : room.experience.slides[room.currentSlide].tipo ===
                      "solo-sfondo" ? null : room.experience.slides[
                        room.currentSlide
                      ].tipo === "doppia-lista" ||
                      room.experience.slides[room.currentSlide].tipo ===
                        "lista-multipla" ||
                      room.experience.slides[room.currentSlide].testo ||
                      room.experience.slides[room.currentSlide].domanda ? (
                      <div
                        style={{
                          textAlign: "center",
                          textTransform: "none",
                          margin: "32px",
                          padding: "32px",
                          borderRadius: "24px",
                          background: "rgba(255, 255, 255, .8)",
                          backdropFilter: "blur(16px)",
                        }}
                      >
                        <Typography
                          variant="h4"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp:
                              Math.floor((nextPageHeight - 128) / 41) || 1,
                            WebkitBoxOrient: "vertical",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: linkifyHtml(
                              sanitizeYaml(
                                room.experience.slides[room.currentSlide]
                                  .tipo === "doppia-lista" ||
                                  room.experience.slides[room.currentSlide]
                                    .tipo === "lista-multipla"
                                  ? generateNaturalLanguageList(
                                      Object.keys(
                                        room.experience.slides[
                                          room.currentSlide
                                        ]
                                      )
                                        .filter((key) =>
                                          key.startsWith("lista-")
                                        )
                                        .map((list) =>
                                          sanitizeYaml(
                                            room.experience.slides[
                                              room.currentSlide
                                            ]["nome-" + list]
                                          )
                                        ),
                                      t
                                    )
                                  : sanitizeYaml(
                                      room.experience.slides[room.currentSlide]
                                        .testo
                                    ) ||
                                      sanitizeYaml(
                                        room.experience.slides[
                                          room.currentSlide
                                        ].domanda
                                      )
                              ),
                              {
                                target: "_blank",
                                attributes: {
                                  class: "underlineOnHover",
                                },
                              }
                            ),
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundImage: `url(${backgrounds[room.currentSlide]})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    className="notSelectable"
                  >
                    {window.location.pathname !== "/admin" && (
                      <>
                        <Fade
                          in={!!room.currentSlide && room.noHost}
                          appear={false}
                        >
                          <ButtonBase
                            style={{
                              position: "absolute",
                              bottom: "16px",
                              left: "16px",
                              textAlign: "center",
                              textTransform: "none",
                              padding: "16px",
                              borderRadius: "12px",
                              background: "rgba(255, 255, 255, .8)",
                              backdropFilter: "blur(16px)",
                            }}
                            onClick={() =>
                              this.changeSlide(
                                queryStringify.parse(window.location.search)
                                  .room,
                                room.participantId,
                                false
                              )
                            }
                          >
                            <ArrowBack
                              style={{ width: "32px", height: "32px" }}
                            />
                          </ButtonBase>
                        </Fade>
                        <Fade
                          in={isVerifyButtonVisible && room.noHost}
                          appear={false}
                        >
                          <ButtonBase
                            style={{
                              width: "64px",
                              height: "64px",
                              position: "absolute",
                              bottom: "16px",
                              right: "16px",
                              textAlign: "center",
                              textTransform: "none",
                              padding: "16px",
                              borderRadius: "12px",
                              background: "rgba(255, 255, 255, .8)",
                              backdropFilter: "blur(16px)",
                            }}
                            onClick={() =>
                              this.checkSolution(
                                queryStringify.parse(window.location.search)
                                  .room,
                                room.participantId,
                                room.currentSlide
                              )
                            }
                            disabled={showWrongAnswerIcon}
                          >
                            {(() => {
                              const iconStyle = {
                                width: "50%",
                                height: "50%",
                                position: "absolute",
                              }

                              return (
                                <>
                                  <Fade
                                    in={!showWrongAnswerIcon}
                                    appear={false}
                                  >
                                    <Send style={iconStyle} />
                                  </Fade>
                                  <Fade in={showWrongAnswerIcon}>
                                    <Clear style={iconStyle} />
                                  </Fade>
                                </>
                              )
                            })()}
                          </ButtonBase>
                        </Fade>
                        <Fade
                          in={
                            ((!isVerifyButtonVisible &&
                              room.currentSlide !==
                                room.experience.slides.length - 1 &&
                              ![
                                "domanda-a-risposta-multipla",
                                "domanda-aperta",
                                "domanda-aperta-multipla",
                              ].includes(
                                room.experience.slides[room.currentSlide].tipo
                              )) ||
                              nextVisible) &&
                            room.noHost
                          }
                          appear={false}
                          style={{
                            transitionDelay: `${theme.transitions.duration.leavingScreen}ms`,
                          }}
                        >
                          <ButtonBase
                            style={{
                              position: "absolute",
                              bottom: "16px",
                              right: "16px",
                              textAlign: "center",
                              textTransform: "none",
                              padding: "16px",
                              borderRadius: "12px",
                              background: "rgba(255, 255, 255, .8)",
                              backdropFilter: "blur(16px)",
                            }}
                            onClick={() =>
                              this.changeSlide(
                                queryStringify.parse(window.location.search)
                                  .room,
                                room.participantId,
                                true
                              )
                            }
                          >
                            <ArrowForward
                              style={{ width: "32px", height: "32px" }}
                            />
                          </ButtonBase>
                        </Fade>
                        {room.experience.slides[room.currentSlide].tipo !==
                          "embed" &&
                          (room.experience.slides[room.currentSlide].tipo ===
                            "doppia-lista" ||
                          room.experience.slides[room.currentSlide].tipo ===
                            "lista-multipla"
                            ? Object.keys(
                                room.experience.slides[room.currentSlide]
                              ).filter((key) => key.startsWith("lista-"))
                                .length === 2 && greaterThan1530
                            : (room.experience.slides[room.currentSlide]
                                .tipo === "solo-testo" ||
                              room.experience.slides[room.currentSlide].tipo ===
                                "solo-immagine"
                                ? greaterThan1307
                                : greaterThan1155) ||
                              room.experience.slides[room.currentSlide].tipo ===
                                "solo-sfondo") && (
                            <div
                              style={{
                                position: "absolute",
                                top: "16px",
                                right: "16px",
                                textAlign: "center",
                                textTransform: "none",
                                padding: "16px",
                                borderRadius: "12px",
                                background: "rgba(255, 255, 255, .8)",
                                backdropFilter: "blur(16px)",
                              }}
                            >
                              <Typography
                                variant="h5"
                                style={{
                                  fontWeight: 700,
                                }}
                              >
                                {secondsToTime(timer).toString().length > 7
                                  ? "59:59+"
                                  : secondsToTime(timer)}
                              </Typography>
                            </div>
                          )}
                      </>
                    )}
                    {room.experience.slides[room.currentSlide].tipo ===
                    "solo-testo" ? (
                      <TextOnly
                        text={room.experience.slides[room.currentSlide].testo}
                        textPosition={
                          room.experience.slides[room.currentSlide][
                            "posizione-testo"
                          ]
                        }
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                      "embed" ? (
                      <Embed
                        url={room.experience.slides[room.currentSlide].url}
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                        "indizi-diversi" &&
                      window.location.pathname !== "/admin" ? (
                      <UserDifferentClues
                        text={room.experience.slides[room.currentSlide].testo}
                        clue={room.experience.slides[room.currentSlide].indizio}
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                      "domanda-a-risposta-multipla" ? (
                      <MultipleChoice
                        text={room.experience.slides[room.currentSlide].domanda}
                        risposte={
                          room.experience.slides[room.currentSlide].risposte
                        }
                        participantId={room.participantId}
                        slideState={room.slideStates[room.currentSlide]}
                        slideNumber={room.currentSlide}
                        setNextVisible={() =>
                          this.setState({ nextVisible: true })
                        }
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                      "domanda-aperta-multipla" ? (
                      <MultipleOpenEnded
                        text={room.experience.slides[room.currentSlide].domanda}
                        risposte={
                          room.experience.slides[room.currentSlide].risposte
                        }
                        participantId={room.participantId}
                        slideState={room.slideStates[room.currentSlide]}
                        slideNumber={room.currentSlide}
                        setNextVisible={() =>
                          this.setState({ nextVisible: true })
                        }
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                      "domanda-aperta" ? (
                      <OpenEnded
                        text={room.experience.slides[room.currentSlide].domanda}
                        correct={
                          room.experience.slides[room.currentSlide].risposta
                        }
                        participantId={room.participantId}
                        slideNumber={room.currentSlide}
                        setNextVisible={() =>
                          this.setState({ nextVisible: true })
                        }
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                      "lista-da-ordinare" ? (
                      <SortableList
                        text={room.experience.slides[room.currentSlide].testo}
                        items={room.experience.slides[room.currentSlide].lista}
                        slideState={room.slideStates[room.currentSlide]}
                        participantId={room.participantId}
                        roomCode={
                          window.location.pathname === "/admin"
                            ? room.roomCode
                            : queryStringify.parse(window.location.search).room
                        }
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                        "doppia-lista" ||
                      room.experience.slides[room.currentSlide].tipo ===
                        "lista-multipla" ? (
                      <MultipleLists
                        lists={Object.keys(
                          room.experience.slides[room.currentSlide]
                        )
                          .filter((key) => key.startsWith("lista-"))
                          .map((list) => ({
                            name: room.experience.slides[room.currentSlide][
                              "nome-" + list
                            ],
                            list: room.experience.slides[room.currentSlide][
                              list
                            ],
                          }))}
                        slideState={room.slideStates[room.currentSlide]}
                        participantId={room.participantId}
                        roomCode={
                          window.location.pathname === "/admin"
                            ? room.roomCode
                            : queryStringify.parse(window.location.search).room
                        }
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                      "solo-immagine" ? (
                      <Picture
                        text={room.experience.slides[room.currentSlide].testo}
                        src={pictures[room.currentSlide]}
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                      "indizi-diversi" ? (
                      <DifferentClues
                        clues={room.experience.slides[room.currentSlide].indizi}
                        text={room.experience.slides[room.currentSlide].testo}
                      />
                    ) : room.experience.slides[room.currentSlide].tipo ===
                      "solo-sfondo" ? (
                      <Hotspots
                        hotspots={
                          room.experience.slides[room.currentSlide]
                            .palloncini || []
                        }
                        background={backgrounds[room.currentSlide]}
                        parentAspectRatio={parentAspectRatio}
                      />
                    ) : null}
                  </div>
                )
              ) : (
                <div
                  ref={measureRef}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: isNextPage ? nextPageHeight : "100%",
                  }}
                >
                  <CircularProgress />
                </div>
              )}
            </>
          )
        }
      }
    )
  )
)
